import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { FilterableSelect, type FilterableSelectOption } from '@/components/ui/filterable-select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usersApi, type MirageUser } from '@/lib/api';

interface MirageUserSelectorProps {
  selectedUserId: number | null;
  onSelect: (userId: number | null, username: string | null) => void;
  disabled?: boolean;
}

export function MirageUserSelector({
  selectedUserId,
  onSelect,
  disabled = false,
}: MirageUserSelectorProps) {
  const value = selectedUserId?.toString() || 'none';

  const { data, isLoading, error } = useQuery(
    ['mirage-users'],
    () => usersApi.getMirageUsers(),
    {
      retry: 1,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  const options: FilterableSelectOption[] = useMemo(() => {
    if (!data?.users) return [];
    return data.users.map((user: MirageUser) => ({
      value: user.id.toString(),
      label: user.full_name,
      details: user.username,
      searchText: `${user.full_name} ${user.username}`,
    }));
  }, [data]);

  const handleValueChange = (newValue: string) => {
    if (!newValue || newValue === 'none') {
      onSelect(null, null);
    } else {
      const userId = parseInt(newValue, 10);
      const selectedUser = data?.users.find(u => u.id === userId);
      onSelect(userId, selectedUser?.username || null);
    }
  };

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load accounting software users';
    return (
      <div className="space-y-2">
        <Label htmlFor="mirage-user" className="text-sm font-medium">
          Accounting software user link
        </Label>
        <Alert variant="destructive">
          <AlertDescription className="text-sm">
            {errorMessage}. Please check the client's accounting software database connection.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="mirage-user" className="text-sm font-medium">
        Accounting software user link
        <span className="text-sm text-gray-500 ml-2 font-normal">
          (for audit trail)
        </span>
      </Label>
      <FilterableSelect
        value={value}
        onValueChange={handleValueChange}
        options={options}
        disabled={disabled || isLoading || !data}
        loading={isLoading}
        placeholder="Select Mirage user to link"
        searchPlaceholder="Search by name or username..."
        emptyMessage="No matching users found"
        showClearOption
        clearOptionLabel="No Mirage user link"
        maxVisible={100}
      />
      {data && (
        <p className="text-xs text-gray-500">
          {data.users.length} user{data.users.length !== 1 ? 's' : ''} available from {data.database_name} database
        </p>
      )}
      {value === 'none' && !isLoading && data && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertDescription className="text-xs text-yellow-800">
            Without a Mirage user link, commits will appear as user ID 1 in Mirage.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

